import React, { useState } from 'react';
import { Box, Card, Typography, Button, CircularProgress, Link } from '@mui/material';
import { useNavigate, useLocation } from "react-router-dom";
import useSWRMutation from "swr/mutation";

import { MuiOtpInput } from 'mui-one-time-password-input';
import { useAnalytics } from '../../../context/AnalyticsProvider';
import { useClient } from "../../../context/ClientProvider";
import { useUtils } from "../../../context/UtilsProvider";
import { useAuth } from '../../../context/AuthProvider';

export default function ConfirmOtpCard() {
  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  const [resendMessage, setResendMessage] = useState(null);
  const [resendError, setResendError] = useState(false);
  const { setTag } = useAnalytics();
  const {
    formError,
    setFormError,
    createAlert,
    alertSeverityTypes,
    resetFormState,
    formState
  } = useUtils();

  const {
    postIdvOtpAuth,
    postOtpConfirmation,
    startSession,
    otpUserId,
    setOtpUserId,
    otpSession,
    setOtpSession
  } = useAuth();

  const { trigger: idvTrigger, isMutating: idvIsMutating } = useSWRMutation("/auth/idv-otp", postIdvOtpAuth);
  const { trigger: otpTrigger, isMutating: otpIsMutating } = useSWRMutation("/auth/idv-otp-confirm", postOtpConfirmation);

  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState("");

  const handleChange = (newValue) => {
    setOtp(newValue);
    setFormError(null);
    if (resendMessage) setResendMessage(null);
  };

  const handleOtpComplete = async () => {
    try {
      const otpResponse = await otpTrigger({ username: otpUserId, session: otpSession, otp });
      setFormError(null);
      setTag("login_otp_success", { path: location.pathname });
      startSession(otpResponse.authenticationResult);
      resetFormState();
      navigate("/");
    } catch (error) {
      console.error("OTP confirmation failed:", error);
      setOtp("");
      const message = (
        <>
          The OTP you entered is incorrect or has expired. Please request a new one and try again or contact us if you are having difficulties on{' '}
          <Link href="tel:08003763333">0800 376 3333</Link>{' '}
          (or <Link href="tel:+442034930829">+44 203 493 0829</Link> if you're abroad).
        </>
      );
      setFormError(message);
      createAlert(message, alertSeverityTypes.error);
      setTag("login_otp_failed", { path: location.pathname, reason: error?.code || "unknown" });
    }
  };

  const resendOtp = async () => {
    setFormError(null);
    setResendMessage(null);
    try {
      const request = {
        phoneNumber: `+44${formState.phoneNumber.substring(1)}`,
        dateOfBirth: formState.dateOfBirth,
        postcode: formState.postcode,
      };
      console.info("post idv with otp resend otp request: ", request);
      const response = await idvTrigger(request);
      console.info("post idv with otp resend otp response: ", response);
      if (response && response.challengeName === "CUSTOM_CHALLENGE") {
        setOtpUserId(response.username);
        setOtpSession(response.session);
        setResendError(false);
        setResendMessage("OTP was resent successfully. Please enter the new code.");
        setTag("login_otp_resend_success", { path: location.pathname });
      } else {
        setResendError(true);
        setResendMessage("Hmm... the OTP failed to re-send. If you continue to experience issues logging in, please contact us on 0800 376 3333 (or +44 203 493 0829 if you're abroad)");
        setTag("login_otp_resend_failed", { path: location.pathname });
      }
    } catch (error) {
      console.error("post sign in for resend otp auth error", error);
      setResendError(true);
      setResendMessage("Hmm... the OTP failed to re-send. If you continue to experience issues logging in, please contact us on 0800 376 3333 (or +44 203 493 0829 if you're abroad)");
      setTag("login_otp_resend_failed", { path: location.pathname, reason: error?.code || "unknown" });
    }
  };

  return clientConfig && (
    <Card
      id="otp-card"
      sx={{
        maxWidth: { md: "80%" },
        mx: "auto",
        mb: "350px",
        px: { xs: 2, sm: 3 },
        py: 3,
        borderRadius: "0",
        position: "relative",
        bottom: { xs: "auto", md: "300px" },
        boxShadow: "-9px 10px 4px -1px rgba(0, 0, 0, 0.2)",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: "bold" }}>
          Hello,
        </Typography>
        <Typography component="p" variant="body1">
          We've sent you a one-time passcode (OTP) by SMS to your mobile number. Please enter the 6 digit code below.
        </Typography>
      </Box>

      <Box sx={{ width: '100%', px: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'fit-content' }}>
              <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2, md: 3 } }}>
                <MuiOtpInput
                  value={otp}
                  onChange={handleChange}
                  length={6}
                  autoFocus
                  validateChar={(char) => !isNaN(char)}
                  sx={{
                    '.MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
                    gap: { xs: '8px', sm: '15px', md: '20px' },
                    '.MuiInputBase-root.MuiOutlinedInput-root': { borderRadius: '30%' },
                    '& .MuiInputBase-input': {
                      width: { xs: '20px', sm: '25px', md: '30px' },
                      height: { xs: '20px', sm: '25px', md: '30px' },
                      fontSize: { xs: '14px', md: '20px' },
                      borderRadius: '30%',
                      padding: '5px',
                    },
                  }}
                />
              </Box>
              <Link
                component="button"
                variant="body2"
                onClick={resendOtp}
                sx={{ mt: 1, color: '#000', textDecorationColor: '#000', whiteSpace: 'nowrap' }}
              >
                Resend OTP
              </Link>
            </Box>
          </Box>

          <Box sx={{ minHeight: '40px', maxWidth: '90%' }}>
            {resendMessage && (
              <Typography
                sx={{
                  fontSize: '13px',
                  color: resendError ? '#99004C' : '#008000',
                  textAlign: 'left',
                }}
              >
                {resendMessage}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 4,
          ml: { xs: 1, sm: 10, md: 10 },
          mb: 5,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'flex-start',
          gap: 2,
        }}
      >
        <Button
          type='button'
          onClick={handleOtpComplete}
          variant='contained'
          sx={{
            borderRadius: clientConfig.layout.buttonBorderRadius,
            width: { xs: "100%", sm: "25%" },
            backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
            color: clientConfig.layout.primaryButtonColour,
            "&:hover": {
              backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
              color: clientConfig.layout.hoverPrimaryButtonColour,
            }
          }}
        >
          Continue
        </Button>
        {formError && (
          <Typography
            data-testid="otp-error"
            sx={{ color: '#99004C', fontSize: '13px' }}
          >
            {formError}
          </Typography>
        )}
      </Box>

      {(otpIsMutating || idvIsMutating) && (
        <Box sx={{ position: 'relative', mt: 2 }}>
          <CircularProgress
            size={24}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: "-12px",
              marginLeft: "-12px",
            }}
          />
        </Box>
      )}
    </Card>
  );
}
